import { useState } from "react";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { Link } from "react-router-dom";
// Styles
import "../../styles/pages/ForgotPassword.css";

function ForgotPassword() {
  // Form state
  const [email, setEmail] = useState("");
  // Request state
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  // Firebase authentication instance
  const auth = getAuth();

  // Send the password reset email
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setError(null);
    setSuccess(false);
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email.trim());

      setSuccess(true);
    } catch (err) {
      setError("Failed to send reset email. Please check the email address and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="forgot-password-page position-relative d-flex align-items-center overflow-hidden">
      {/* Subtle background grid */}
      <div
        className="forgot-password-grid position-absolute top-0 start-0 w-100 h-100"
        aria-hidden="true"
      />
      {/* Decorative Benoma background text */}
      <div
        className="forgot-password-background-text position-absolute user-select-none"
        aria-hidden="true"
      >
        RESET
      </div>
      <div className="container position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6 col-xl-5">
            {/* Back to login */}
            <Link
              to="/login"
              className="forgot-password-back d-inline-flex align-items-center gap-2 text-decoration-none mb-4"
            >
              <i className="bi bi-arrow-left" aria-hidden="true" />
              Back to login
            </Link>
            <div className="forgot-password-card">
              {/* Page header */}
              <div className="forgot-password-card-header">
                <div className="d-flex align-items-center justify-content-between gap-3 mb-4">
                  <span className="forgot-password-label font-monospace text-danger">
                    ACCOUNT RECOVERY
                  </span>
                  <span className="forgot-password-status d-flex align-items-center gap-2 font-monospace">
                    <span className="forgot-password-status-dot rounded-circle" />
                    secure
                  </span>
                </div>
                <h1 className="forgot-password-title fw-bold mb-3">Reset password.</h1>
                <p className="forgot-password-description text-secondary mb-0">
                  Enter the email address connected to your Benoma account and we'll send you a
                  password reset link.
                </p>
              </div>
              {/* Password reset form */}
              <div className="forgot-password-card-body">
                {/* Error message */}
                {error && (
                  <div
                    className="forgot-password-alert forgot-password-alert-error d-flex align-items-start gap-3 mb-4"
                    role="alert"
                  >
                    <i className="bi bi-exclamation-circle text-danger" aria-hidden="true" />
                    <div>
                      <span className="d-block fw-semibold mb-1">Unable to send reset link</span>
                      <small>{error}</small>
                    </div>
                  </div>
                )}
                {/* Success message */}
                {success && (
                  <div
                    className="forgot-password-alert forgot-password-alert-success d-flex align-items-start gap-3 mb-4"
                    role="status"
                  >
                    <i className="bi bi-check-circle" aria-hidden="true" />
                    <div>
                      <span className="d-block fw-semibold mb-1">Check your inbox</span>
                      <small>
                        If an account exists for <strong>{email.trim()}</strong>, a password reset
                        link has been sent.
                      </small>
                    </div>
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  {/* Email */}
                  <div className="mb-4">
                    <label htmlFor="reset-email" className="forgot-password-form-label form-label">
                      Email address
                    </label>
                    <div className="forgot-password-input-wrapper position-relative">
                      <i
                        className="bi bi-envelope forgot-password-input-icon position-absolute"
                        aria-hidden="true"
                      />
                      <input
                        type="email"
                        id="reset-email"
                        className="form-control forgot-password-input"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          // Clear the previous success state if the email changes
                          if (success) setSuccess(false);
                        }}
                        autoComplete="email"
                        inputMode="email"
                        autoCapitalize="none"
                        spellCheck="false"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>
                  {/* Submit */}
                  <button
                    type="submit"
                    className="btn btn-danger forgot-password-submit w-100 d-flex align-items-center justify-content-center gap-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                        <span>Sending reset link...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Reset Link</span>
                        <i className="bi bi-arrow-right" aria-hidden="true" />
                      </>
                    )}
                  </button>
                </form>
                {/* Additional login link */}
                <div className="text-center mt-4">
                  <span className="forgot-password-login-text text-secondary">
                    Remember your password?
                  </span>{" "}
                  <Link to="/login" className="forgot-password-login-link text-decoration-none">
                    Sign in
                  </Link>
                </div>
              </div>
              {/* Terminal-style footer */}
              <div className="forgot-password-card-footer font-monospace">
                <span className="text-danger">›</span>
                <span className="text-secondary"> password recovery</span>
                <span className="forgot-password-terminal-cursor" aria-hidden="true">
                  _
                </span>
              </div>
            </div>
            {/* Portal label */}
            <p className="forgot-password-footer-text text-center text-secondary font-monospace mt-4 mb-0">
              BENOMA / ACCOUNT RECOVERY
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}

export default ForgotPassword;
